import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, faqs, mainSiteUrl } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const homepageArticleSlugs = [
  'best-sugargoo-spreadsheet-beginners',
  'how-to-find-quality-products-sugargoo',
  'sugargoo-qc-photos-guide',
  'sugargoo-warehouse-guide',
  'sugargoo-spreadsheet-trending-finds',
  'sugargoo-review-2026',
  'sugargoo-shipping-cost-guide',
  'sugargoo-coupon-discount-guide',
  'how-to-save-money-on-sugargoo',
];

const homepageArticles = articles.filter((a) => homepageArticleSlugs.includes(a.slug));

export default function Home() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener,nofollow');
  };

  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full min-h-[620px] md:min-h-[720px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Stylish%20fashion%20lifestyle%20flatlay%20with%20diverse%20neatly%20folded%20clothing%20items%2C%20sneakers%2C%20accessories%20arranged%20harmoniously%20on%20a%20warm%20cream%20textured%20surface%2C%20soft%20diffused%20natural%20window%20light%20creating%20gentle%20shadows%2C%20editorial%20product%20photography%20with%20organic%20composition%2C%20neutral%20warm%20tones%2C%20minimalist%20European%20aesthetic%2C%20subtle%20linen%20texture%20background&width=1600&height=900&seq=hero-2026-v2&orientation=landscape"
            alt="Sugargoo Spreadsheet product showcase background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/35"></div>
        </div>

        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse"></span>
            <span className="text-xs text-background-50 font-medium tracking-wide">UPDATED PRODUCT DATABASE</span>
          </div>

          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 leading-tight mb-6">
            The Ultimate <strong className="font-heading">Sugargoo Spreadsheet</strong> 2026
          </h1>

          <p className="text-base md:text-lg text-background-50/85 max-w-2xl mx-auto mb-10 leading-relaxed">
            Find thousands of curated products, verified finds, QC photos, shopping guides, and money-saving tips through our complete Sugargoo Spreadsheet database — your trusted resource for smarter shopping.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-base hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shopping-cart-2-line"></i>
              Sugargoo Spreadsheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-accent-500 text-background-50 font-semibold text-base hover:bg-accent-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-flashlight-line"></i>
              Buy It Now
            </a>
          </div>

          {/* Category Dropdown */}
          <div ref={dropdownRef} className="relative w-full max-w-md mx-auto">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between gap-3 px-6 py-3.5 rounded-full bg-background-50/95 backdrop-blur-sm border border-background-200/60 text-foreground-800 hover:bg-background-50 transition-colors cursor-pointer"
            >
              <span className="text-sm font-medium whitespace-nowrap">
                {selectedCategory || 'Browse Product Categories'}
              </span>
              <i className={`${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500 transition-transform`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 rounded-2xl border border-background-200/60 shadow-lg overflow-hidden z-50 max-h-[360px] overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategorySelect(cat)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left text-sm text-foreground-700 hover:bg-background-100 transition-colors border-b border-background-100 last:border-b-0 cursor-pointer"
                  >
                    <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                      <i className={cat.icon}></i>
                    </span>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {['Updated Product Database', 'Beginner Friendly Guides', 'Free Shopping Resources', 'Independent Platform'].map((text) => (
              <div key={text} className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-500/20">
                  <i className="ri-check-line text-xs text-accent-400"></i>
                </span>
                <span className="text-xs text-background-50/75 whitespace-nowrap">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Grid with Images */}
      <section id="categories" className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold tracking-wide mb-4">EXPLORE</span>
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4">
              Sugargoo Spreadsheet Categories
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base">
              Browse through our organized product categories and discover trending finds across fashion, accessories, and more.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
            {categories.map((cat) => (
              <a
                key={cat.slug}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group flex flex-col items-center gap-3 p-3 md:p-4 rounded-xl bg-background-100 hover:bg-secondary-50 border border-background-200/50 hover:border-secondary-200 transition-all duration-200 cursor-pointer overflow-hidden"
              >
                <div className="w-full aspect-square rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={cat.imageUrl}
                    alt={`${cat.name} category`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 group-hover:bg-secondary-200 group-hover:text-secondary-800 transition-colors flex-shrink-0">
                    <i className={`${cat.icon} text-sm`}></i>
                  </span>
                  <span className="text-xs md:text-sm text-foreground-700 font-medium leading-tight whitespace-nowrap">
                    {cat.name}
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* What Is Sugargoo Spreadsheet */}
      <section id="what-is" className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold tracking-wide mb-4">ABOUT</span>
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-6">
                What Is <strong className="font-heading">Sugargoo Spreadsheet</strong>?
              </h2>
              <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                <p>
                  Sugargoo Spreadsheet is an organized product discovery database designed to help shoppers find popular products faster and smarter. Instead of spending hours manually searching through thousands of listings across different platforms, users can explore carefully categorized spreadsheets that contain verified product information, shopping recommendations, and real community feedback.
                </p>
                <p>
                  Each spreadsheet entry typically includes product names, direct purchase links, pricing estimates, quality control photo examples, size references, and honest reviews from experienced buyers. This structured approach transforms chaotic online shopping into a streamlined, confidence-boosting experience for both beginners and seasoned shoppers.
                </p>
                <p>
                  Whether you are hunting for the latest sneaker releases, curated streetwear pieces, luxury-inspired accessories, or budget-friendly everyday essentials, a well-maintained Sugargoo Spreadsheet acts as your personal shopping assistant — saving you time, reducing guesswork, and helping you make informed purchasing decisions.
                </p>
              </div>
              <Link
                to="/article/sugargoo-spreadsheet-ultimate-guide"
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full border-2 border-primary-500 text-primary-600 font-semibold text-sm hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Read Full Guide
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20clean%20workspace%20with%20laptop%20displaying%20organized%20spreadsheet%20dashboard%2C%20warm%20natural%20light%20from%20large%20window%2C%20cream%20and%20beige%20minimalist%20interior%2C%20desk%20with%20notebook%20and%20coffee%2C%20soft%20shadows%2C%20editorial%20productivity%20lifestyle%20photography%2C%20organized%20and%20serene%20atmosphere&width=800&height=600&seq=what-is-2026-v2&orientation=landscape"
                alt="Sugargoo Spreadsheet database interface on laptop"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Use It */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wide mb-4">BENEFITS</span>
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4">
              Why Thousands Use Sugargoo Spreadsheet
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base">
              Discover why savvy shoppers rely on organized spreadsheets for their product research.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              { icon: 'ri-time-line', title: 'Save Time', desc: 'Skip endless scrolling and discover trending products instantly through organized categories and verified links.' },
              { icon: 'ri-search-eye-line', title: 'Better Research', desc: 'Compare products side by side with detailed information, real QC photos, and community feedback before purchasing.' },
              { icon: 'ri-user-heart-line', title: 'Beginner Friendly', desc: 'Learn how Sugargoo works step by step with clear guides, making your first purchase smooth and stress-free.' },
              { icon: 'ri-lightbulb-flash-line', title: 'Smart Shopping', desc: 'Find better value products, discover hidden gems, and stay updated with the latest community-curated finds.' },
            ].map((item) => (
              <div key={item.title} className="p-6 md:p-7 rounded-xl bg-background-100 border border-background-200/60 hover:border-secondary-200 transition-all duration-200">
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-4">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9 SEO Article Cards */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold tracking-wide mb-4">IN-DEPTH GUIDES</span>
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4">
              Expert Sugargoo Spreadsheet Guides & Resources
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base">
              Dive deep into our comprehensive collection of independent guides, reviews, and expert tips covering every aspect of Sugargoo shopping.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {homepageArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group block rounded-xl bg-background-50 border border-background-200/60 hover:border-secondary-200 overflow-hidden transition-all duration-200 cursor-pointer"
              >
                <div className="aspect-[16/10] overflow-hidden bg-background-200">
                  <img
                    src={article.featuredImage}
                    alt={article.featuredImageAlt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-3">{article.badge}</span>
                  <h3 className="font-heading text-base md:text-lg text-foreground-900 mb-2 leading-snug group-hover:text-foreground-950 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                    {article.description}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-sm text-primary-600 font-medium">
                    <span>Read Article</span>
                    <i className="ri-arrow-right-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/article/sugargoo-spreadsheet-ultimate-guide"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-primary-500 text-primary-600 font-semibold text-sm hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              View All Guides
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold tracking-wide mb-4">PROCESS</span>
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4">
              How Sugargoo Spreadsheet Works
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base">
              Five simple steps from browsing to receiving your package.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 md:gap-6">
            {[
              { step: '01', icon: 'ri-folder-open-line', title: 'Browse Categories', desc: 'Explore organized product categories and find items that match your style and budget.' },
              { step: '02', icon: 'ri-checkbox-circle-line', title: 'Select Products', desc: 'Review detailed product info, QC photos, and community ratings to make confident choices.' },
              { step: '03', icon: 'ri-image-line', title: 'Check QC Info', desc: 'Examine quality control photos and real buyer feedback before finalizing your selection.' },
              { step: '04', icon: 'ri-shopping-bag-3-line', title: 'Purchase Via Platform', desc: 'Use product links to complete your order through the official Sugargoo platform securely.' },
              { step: '05', icon: 'ri-truck-line', title: 'Receive Package', desc: 'Track your shipment and receive your carefully packaged items at your doorstep.' },
            ].map((item) => (
              <div key={item.step} className="relative p-6 rounded-xl bg-background-100 border border-background-200/60">
                <span className="text-4xl font-heading text-secondary-300 absolute top-4 right-4">{item.step}</span>
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4 relative z-10">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <h3 className="font-heading text-base text-foreground-900 mb-2 relative z-10">{item.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed relative z-10">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Article CTA */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="relative rounded-2xl overflow-hidden p-10 md:p-14 bg-background-50 border border-background-200/60">
            <div className="relative z-10 text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wide mb-4">IN-DEPTH GUIDE</span>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
                Sugargoo Spreadsheet: The Ultimate 2026 Guide
              </h2>
              <p className="text-foreground-500 max-w-lg mx-auto mb-8 text-sm md:text-base leading-relaxed">
                Read our comprehensive guide covering everything from getting started with Sugargoo Spreadsheet to advanced shopping strategies, shipping optimization, coupon tips, and platform safety recommendations.
              </p>
              <Link
                to="/article/sugargoo-spreadsheet-ultimate-guide"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-base hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-article-line"></i>
                Read the Complete Guide
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold tracking-wide mb-4">FAQ</span>
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base">
              Everything you need to know about Sugargoo Spreadsheet.
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="rounded-xl bg-background-100 border border-background-200/60 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer hover:bg-background-50/50 transition-colors"
                >
                  <h3 className="font-heading text-base md:text-lg text-foreground-900 pr-4">{faq.question}</h3>
                  <span className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 transition-transform duration-200 ${openFaq === idx ? 'rotate-45' : ''}`}>
                    <i className="ri-add-line"></i>
                  </span>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5">
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* WebSite Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Sugargoo Spreadsheet 2026 | Ultimate Finds Database & Shopping Guide",
            "url": "https://sugargoosheet.xyz",
            "description": "Discover the best Sugargoo Spreadsheet 2026 with updated finds, categorized product database, QC photos, shopping guides, shipping tips, reviews, coupons, and beginner tutorials.",
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://sugargoosheet.xyz/search?q={search_term_string}"
              },
              "query-input": "required name=search_term_string"
            }
          })
        }}
      />

      {/* Organization Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "sugargoosheet.xyz",
            "url": "https://sugargoosheet.xyz",
            "logo": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg",
            "description": "Independent informational resource about Sugargoo Spreadsheet — helping shoppers discover verified products, QC photos, and shopping guides.",
            "sameAs": [
              "https://kaspreadsheet.com"
            ]
          })
        }}
      />

      {/* FAQ Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is Sugargoo Spreadsheet?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sugargoo Spreadsheet is an organized product discovery database designed to help shoppers find popular products faster. Instead of manually searching through thousands of listings, users can explore categorized spreadsheets containing verified product information, shopping recommendations, QC photo examples, and detailed buying guides."
                }
              },
              {
                "@type": "Question",
                "name": "Is Sugargoo legit?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, Sugargoo is a legitimate shopping platform that has served thousands of international buyers. The platform offers warehouse storage, quality control photo services, and reliable shipping options."
                }
              },
              {
                "@type": "Question",
                "name": "How much does Sugargoo shipping cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sugargoo shipping costs vary based on package weight, destination country, shipping method selected, and warehouse storage duration. Economy shipping lines are more affordable while express options cost more but deliver faster."
                }
              },
              {
                "@type": "Question",
                "name": "How do I use Sugargoo Spreadsheet?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Browse through the organized categories to find products, review product details and QC photos, copy product links to paste into the Sugargoo platform, and complete your purchase with confidence."
                }
              },
              {
                "@type": "Question",
                "name": "Does Sugargoo have coupons or discount codes?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, Sugargoo occasionally offers coupons and discount codes for shipping fees or service charges. Promotions are announced through official channels and community forums."
                }
              },
              {
                "@type": "Question",
                "name": "Where is Sugargoo warehouse located?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sugargoo operates warehouse facilities in China where products are received, inspected, and stored before international shipping, allowing for quality control photo services and order consolidation."
                }
              }
            ]
          })
        }}
      />
    </>
  );
}